import React, { useCallback, useLayoutEffect, useState, useMemo } from "react";

import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { useIntl } from "react-intl";
import { TeamIdentifiersComponent } from "../TeamIdentifiers/TeamIdentifiersComponent";
import { teamColors } from "../TeamIdentifiers/teamColors";
import { onlyUnique } from "../../utils";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TeamCreateQuery } from "./__generated__/TeamCreateQuery.graphql";
import { TeamIcon } from "../TeamIdentifiers/teamIcons";
import { useAlert } from "../../common/Alert";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { AHSeparator, AHSwitch, ListItem } from "arkh";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { TeamCreateMutation } from "./__generated__/TeamCreateMutation.graphql";

export const TeamCreate = withWebModalStack(TeamCreateC);

function TeamCreateC(props: UserLoggedInNavigatorScreenProps<"TeamCreate">) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerTeamCreate {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerTeamCreate({
  navigation,
  fetchKey,
}: UserLoggedInNavigatorScreenProps<"TeamCreate"> & { fetchKey: string }) {
  const data = useLazyLoadQuery<TeamCreateQuery>(
    graphql`
      query TeamCreateQuery {
        store {
          id
          publicTeams {
            icon
            color
          }
        }
      }
    `,
    {},
    { fetchKey }
  );

  const usedColors = useMemo(
    () =>
      data?.store.publicTeams
        .flatMap(({ color }) => (color ? [color] : []) as string[])
        .filter(onlyUnique) ?? [],
    [data?.store.publicTeams]
  );
  const usedIcons = useMemo(
    () =>
      data?.store.publicTeams
        .flatMap(({ icon }) => (icon ? [icon] : []))
        .filter(onlyUnique) ?? [],
    [data?.store.publicTeams]
  );

  return (
    <TeamCreateComponent
      navigation={navigation}
      usedColors={usedColors}
      usedIcons={usedIcons}
    />
  );
}

function TeamCreateComponent(props: {
  navigation: UserLoggedInNavigatorScreenProps<"TeamCreate">["navigation"];
  usedColors: string[];
  usedIcons: TeamIcon[];
}) {
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const unusedColors = useMemo(
    () => teamColors.filter((color) => !props.usedColors.includes(color)),
    [props.usedColors]
  );
  const [color, setColor] = useState<string>(
    unusedColors.length > 0
      ? unusedColors[Math.round(Math.random() * (unusedColors.length - 1))]
      : teamColors[Math.round(Math.random() * (teamColors.length - 1))]
  );
  const [icon, setIcon] = useState<TeamIcon>("default");

  const intl = useIntl();
  const intlTokens = useIntlTokens();

  const [createTeam, loading] = useMutation<TeamCreateMutation>(graphql`
    mutation TeamCreateMutation($input: TeamCreateInput!) {
      store {
        teamCreate(input: $input) {
          team {
            id
            isPrivate
          }
          store {
            id
            publicTeams {
              id
              name
              color
              icon
              isPrivate
            }
          }
        }
      }
    }
  `);

  const Alert = useAlert();

  const onCreateTeam = useCallback(() => {
    createTeam({
      variables: {
        input: {
          name,
          color,
          icon,
          isPrivate,
        },
      },
      onCompleted: (d) => {
        if (d.store.teamCreate.team.isPrivate) {
          props.navigation.replace("TeamCreateWhitelistUser", {
            teamId: d.store.teamCreate.team.id,
          });
        } else {
          props.navigation.goBack();
        }
      },
      onError: () => {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Failed to create team",
            description: "Failed to create team alert title",
          })
        );
      },
    });
  }, [createTeam, name, color, icon, isPrivate, props.navigation, Alert, intl]);

  const getOptions = useNavigationHeaderOptions();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "CREATE",
        onSave: onCreateTeam,
        onCancel: () => props.navigation.goBack(),
        saveLoading: loading,
        title: intl.formatMessage({
          defaultMessage: "Create Team",
          description: "Team Create header title",
        }),
      })
    );
  }, [getOptions, intl, loading, onCreateTeam, props.navigation]);

  return (
    <TeamIdentifiersComponent
      usedIcons={props.usedIcons}
      usedColors={props.usedColors}
      mode="create"
      color={color}
      icon={icon}
      name={name}
      setName={setName}
      setIcon={setIcon}
      setColor={setColor}
      isPrivate={isPrivate}
      LockOption={
        <>
          <AHSeparator />
          <ListItem
            right={<AHSwitch value={isPrivate} onValueChange={setIsPrivate} />}
          >
            <ListItem.Title>{intlTokens.LockedTeam}</ListItem.Title>
          </ListItem>
        </>
      }
    />
  );
}
